"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  buildCaseAnalysis,
  type CaseInput,
  FRAMEWORKS,
  type Framework,
} from "@/lib/tool-kit/business-templates/case-analysis";

/** English strings. A translated content file overrides these through `ui`. */
export const CASE_STUDY_ANALYSIS_OUTLINE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  caseHeading: "The case",
  titleLabel: "Case title",
  titlePlaceholder: "e.g. Brightside Coffee Roasters",
  decisionMakerLabel: "Who has to decide, and where",
  decisionMakerPlaceholder: "e.g. Maya Chen, founder and CEO of Brightside",
  decisionLabel: "The decision, as a question",
  decisionPlaceholder:
    "e.g. Should Brightside open a second café or sell online instead?",
  factsLabel: "Key facts from the case — one per line",
  factsPlaceholder: "e.g. Revenue grew 40% last year, mostly from one café",
  problemLabel: "The problem, in your own words",
  problemPlaceholder:
    "The underlying problem, not the symptoms. One or two sentences.",
  alternativesHeading: "Alternatives and criteria",
  alternativesLabel: "Alternatives — one per line",
  alternativesPlaceholder: "e.g. Open a second café",
  criteriaLabel: "Criteria to judge them by — one per line, up to six",
  criteriaPlaceholder: "e.g. Cost",
  criteriaHint:
    "Leave this empty to use fit with the problem, feasibility, cost, and risk.",
  recommendationLabel: "Your recommendation (optional)",
  recommendationPlaceholder:
    "Which alternative, and the main reason, in a sentence or two.",
  frameworksLabel: "Frameworks to use in the analysis",
  wordsLabel: "Target length (words)",
  wordsPlaceholder: "e.g. 2000",
  wordsHint: "Splits the analysis into a word target for each section.",
  fileName: "case-analysis-outline",
  document: {
    heading: "Your case analysis outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Case Analysis Outline",
  titleWithName: "Case Analysis: {name}",
  subtitle:
    "Situation, problem, analysis, alternatives, recommendation, implementation",
  budgetHeading: "Word Budget",
  budgetNote:
    "A planning convention, not a standard. If your instructor gives a template or page limit, follow that and use these shares only to balance the length.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sectionNames: {
    situation: "Situation and Key Facts",
    problem: "Problem Statement",
    analysis: "Analysis",
    alternatives: "Alternatives",
    recommendation: "Recommendation",
    implementation: "Implementation and Risks",
  },
  tips: {
    situation:
      "Give only the facts the reader needs to follow your argument. Do not retell the whole case.",
    problem:
      "Separate the problem from its symptoms: falling sales may be a symptom, and the problem is why.",
    analysis:
      "Use the frameworks to find causes, then tie every finding back to the problem. Analysis that does not change the decision does not belong.",
    alternatives:
      "Offer at least two realistic options and judge them against the same criteria.",
    recommendation:
      "Choose one alternative, say why it beats the others, and say what you give up by choosing it.",
    implementation:
      "A recommendation without a plan is a wish. Say who does what, by when, and how you will know it worked.",
  },
  target: "Target: {words} words ({share}).",
  targetShare: "About {share} of the analysis.",
  fields: {
    decisionMaker: "Decision maker",
    decision: "Decision",
  },
  prompts: {
    situation:
      "Name the decision maker and organization, and state the decision to be made.",
    facts:
      "List the five to ten facts from the case that matter most — with numbers where the case gives them.",
    problem:
      "What is the underlying problem? What are the symptoms, and what causes them?",
    noFramework:
      "Choose at least one framework above, or say how else you will analyze the case.",
    alternatives:
      "List the realistic options the decision maker has, including doing nothing if it is a real option.",
    needTwo:
      "Add at least one more alternative: a choice needs something to be chosen over.",
    recommendation:
      "Which alternative do you recommend, and why does it beat the others on your criteria?",
    actions:
      "List the first actions, who owns each, and when. Fill the table, then summarize it here.",
    risks: "What could go wrong, how likely is it, and what is the fallback?",
    measures:
      "What will you measure to know within a few months that the decision is working?",
  },
  frameworkNames: {
    swot: "SWOT",
    pestle: "PESTLE",
    forces: "Five Forces",
    financial: "Financial analysis",
    stakeholders: "Stakeholders",
  },
  frameworkLabels: {
    swot: "SWOT — strengths, weaknesses, opportunities, threats",
    pestle: "PESTLE — the outside environment",
    forces: "Porter's five forces — the industry",
    financial: "Financial analysis — the numbers in the case",
    stakeholders: "Stakeholders — who gains and loses",
  },
  frameworkPrompts: {
    swot: "Which strengths and weaknesses of the organization, and which outside opportunities and threats, bear on this decision? Rank them.",
    pestle:
      "Which political, economic, social, technological, legal, or environmental changes affect the decision?",
    forces:
      "Which of the five forces — entrants, suppliers, buyers, substitutes, rivalry — presses hardest on profit in this industry?",
    financial:
      "What do the case's figures show — growth, margins, cash, break-even? Show the calculation behind each number you use.",
    stakeholders:
      "Who is affected by each alternative, and how much power and interest does each group have?",
  },
  tableHeads: {
    alternative: "Alternative",
    actions: ["Action", "Who", "When", "How you will know it worked"],
  },
  defaultCriteria: ["Fit with the problem", "Feasibility", "Cost", "Risk"],
  checklistHeading: "Before You Submit",
  checklist: [
    "The problem is stated as a cause, not a symptom",
    "Every analysis point connects to the problem or the decision",
    "The alternatives are different from each other and judged on the same criteria",
    "The recommendation names one alternative and what it gives up",
    "Every figure from the case is quoted correctly and every outside source is cited",
  ],
  example: {
    title: "Brightside Coffee Roasters",
    decisionMaker: "Maya Chen, founder and CEO of Brightside",
    decision: "Should Brightside open a second café or sell online instead?",
    facts:
      "Revenue grew 40% last year, mostly from the original café\nThe café is full on weekday mornings and empty after 2 p.m.\nWholesale orders from local offices doubled\nA second site would cost about £120,000 to fit out",
    problem:
      "Brightside's growth depends on one site that is already full at peak times, so growth now needs new capacity or a new channel.",
    alternatives:
      "Open a second café\nSell beans and subscriptions online\nGrow wholesale to offices\nDo nothing",
    criteria: "Up-front cost\nSpeed to first revenue\nFit with the brand\nRisk",
    recommendation: "",
    frameworks: {
      swot: true,
      pestle: false,
      forces: false,
      financial: true,
      stakeholders: false,
    },
    words: "2000",
  },
} as const;

const EMPTY: CaseInput = {
  title: "",
  decisionMaker: "",
  decision: "",
  facts: "",
  problem: "",
  alternatives: "",
  criteria: "",
  recommendation: "",
  frameworks: {
    swot: true,
    pestle: false,
    forces: false,
    financial: true,
    stakeholders: false,
  },
  words: "",
};

type TextKey = Exclude<keyof CaseInput, "frameworks">;

export default function CaseStudyAnalysisOutlineWidget() {
  const strings = useToolUi(CASE_STUDY_ANALYSIS_OUTLINE_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<CaseInput>(EMPTY);
  const doc = useMemo(
    () => buildCaseAnalysis(input, strings, fmt),
    [input, strings, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const setFramework = (key: Framework) => (checked: boolean) =>
    setInput((previous) => ({
      ...previous,
      frameworks: { ...previous.frameworks, [key]: checked },
    }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.caseHeading}>
        <TextField
          id="ca-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <TextField
          id="ca-maker"
          label={strings.decisionMakerLabel}
          onChange={update("decisionMaker")}
          placeholder={strings.decisionMakerPlaceholder}
          value={input.decisionMaker}
        />
        <TextField
          id="ca-decision"
          label={strings.decisionLabel}
          onChange={update("decision")}
          placeholder={strings.decisionPlaceholder}
          value={input.decision}
        />
        <TextAreaField
          id="ca-facts"
          label={strings.factsLabel}
          onChange={update("facts")}
          placeholder={strings.factsPlaceholder}
          prose
          rows={4}
          value={input.facts}
        />
        <TextAreaField
          id="ca-problem"
          label={strings.problemLabel}
          onChange={update("problem")}
          placeholder={strings.problemPlaceholder}
          prose
          rows={3}
          value={input.problem}
        />
      </Panel>

      <Panel title={strings.alternativesHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            id="ca-alternatives"
            label={strings.alternativesLabel}
            onChange={update("alternatives")}
            placeholder={strings.alternativesPlaceholder}
            prose
            rows={4}
            value={input.alternatives}
          />
          <TextAreaField
            hint={strings.criteriaHint}
            id="ca-criteria"
            label={strings.criteriaLabel}
            onChange={update("criteria")}
            placeholder={strings.criteriaPlaceholder}
            prose
            rows={4}
            value={input.criteria}
          />
        </div>
        <TextAreaField
          id="ca-recommendation"
          label={strings.recommendationLabel}
          onChange={update("recommendation")}
          placeholder={strings.recommendationPlaceholder}
          prose
          rows={2}
          value={input.recommendation}
        />
      </Panel>

      <Panel title={strings.frameworksLabel}>
        <div className="grid gap-2 sm:grid-cols-2">
          {FRAMEWORKS.map((key) => (
            <Checkbox
              checked={input.frameworks[key]}
              key={key}
              label={strings.frameworkLabels[key]}
              onChange={setFramework(key)}
            />
          ))}
        </div>
        <TextField
          hint={strings.wordsHint}
          id="ca-words"
          label={strings.wordsLabel}
          numeric
          onChange={update("words")}
          placeholder={strings.wordsPlaceholder}
          value={input.words}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ca"
        labels={strings.document}
      />
    </div>
  );
}
